/**
 * BR HERO — view.js
 * Handles:
 *   - Countdown timer
 *   - Lottie animation
 *   - Background slider
 *   - Video autoplay
 *   - Corner logos — stack en colonne si collision 2D réelle avec le countdown
 *   - Buttons — nowrap sur desktop si ça tient, colonne pleine largeur sur mobile
 *
 * Enterprise safe:
 *   - No memory leaks
 *   - Gutenberg / Ajax safe
 *   - Multi instances ready
 *   - Lottie script loaded on-demand (only if needed)
 */


/* ═══════════════════════════════════════════════════════════
   COUNTDOWN
═══════════════════════════════════════════════════════════ */

class BRHeroCountdown {

	constructor(el) {

		this.el = el;

		if (this.el.dataset.loaded === "1") return;
		this.el.dataset.loaded = "1";

		const ts = parseInt(el.dataset.timestamp, 10);

		if (!ts || Number.isNaN(ts)) {
			console.warn("BR Hero: invalid countdown timestamp ->", el.dataset.timestamp);
			return;
		}

		this.target = ts; // already in ms
		this.start();
	}

	start() {
		this.tick();
		this.interval = window.setInterval(() => this.tick(), 1000);

		this.observer = new MutationObserver(() => {
			if (!document.body.contains(this.el)) this.destroy();
		});

		this.observer.observe(document.body, { childList: true, subtree: true });
	}

	destroy() {
		if (this.interval) { window.clearInterval(this.interval); this.interval = null; }
		if (this.observer) { this.observer.disconnect(); this.observer = null; }
	}

	pad2(n) { return String(n).padStart(2, "0"); }

	tick() {
		const diff = this.target - Date.now();

		if (diff <= 0) {
			this.el.style.display = "none";
			this.el.closest(".br-hero")?.classList.remove("br-hero--has-countdown");
			this.destroy();
			// Recalcule le layout maintenant que le countdown a disparu
			adjustCornerLogos();
			adjustHeroButtons();
			return;
		}

		const days    = Math.floor(diff / 86400000);
		const hours   = Math.floor(diff / 3600000) % 24;
		const minutes = Math.floor(diff / 60000) % 60;
		const seconds = Math.floor(diff / 1000) % 60;

		this.render(days, hours, minutes, seconds);
	}

	render(d, h, m, s) {
		this.el.innerHTML = `
			<div class="br-countdown" role="timer" aria-live="polite">
				${this.unit(d, "d", false)}
				${this.unit(h, "h", true)}
				${this.unit(m, "m", true)}
				${this.unit(s, "s", true)}
			</div>
		`;
	}

	unit(val, label, pad) {
		const value = pad ? this.pad2(val) : String(val);
		return `
			<div class="br-countdown__unit">
				<span class="br-countdown__number">${value}</span>
				<span class="br-countdown__label">${label}</span>
			</div>
		`;
	}
}

function initBRHeroCountdowns(root = document) {
	root.querySelectorAll(".br-hero__countdown").forEach(el => new BRHeroCountdown(el));
}


/* ═══════════════════════════════════════════════════════════
   LOTTIE
═══════════════════════════════════════════════════════════ */

const _scriptPromises = {};

function loadScript(src) {
	if (_scriptPromises[src]) return _scriptPromises[src];

	_scriptPromises[src] = new Promise((resolve, reject) => {

		if (window.lottie) { resolve(window.lottie); return; }

		const existing = Array.from(document.querySelectorAll("script[src]"))
			.find(s => s.getAttribute("src") === src);

		if (existing) {
			let tries = 0;
			const poll = setInterval(() => {
				tries++;
				if (window.lottie) {
					clearInterval(poll);
					resolve(window.lottie);
				} else if (tries > 50) {
					clearInterval(poll);
					reject(new Error(`BR Hero: window.lottie not found after script load: ${src}`));
				}
			}, 100);
			return;
		}

		const s    = document.createElement("script");
		s.src      = src;
		s.async    = true;

		s.onload = () => {
			requestAnimationFrame(() => {
				if (window.lottie) {
					resolve(window.lottie);
				} else {
					reject(new Error(`BR Hero: window.lottie undefined after load. Wrong script URL? ${src}`));
				}
			});
		};

		s.onerror = () => {
			delete _scriptPromises[src];
			reject(new Error(`BR Hero: cannot load Lottie script (network error): ${src}`));
		};

		document.head.appendChild(s);
	});

	return _scriptPromises[src];
}


class BRHeroLottie {

	constructor(el) {

		this.el = el;
		this.anim = null;

		if (this.el.dataset.lottieLoaded === "1") return;
		this.el.dataset.lottieLoaded = "1";

		const url    = el.dataset.lottieUrl;
		const script = el.dataset.lottieScript ||
			"https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie.min.js";

		if (!url) {
			console.warn("BR Hero Lottie: missing data-lottie-url");
			return;
		}

		const isLottiePlayer = script.includes("lottie-player");

		if (isLottiePlayer) {
			this._initWebComponent(script, url);
		} else {
			loadScript(script)
				.then(() => this._initLottieWeb(url))
				.catch(err => console.warn("BR Hero Lottie:", err));
		}

		this._observer = new MutationObserver(() => {
			if (!document.body.contains(this.el)) this.destroy();
		});
		this._observer.observe(document.body, { childList: true, subtree: true });
	}

	_initWebComponent(scriptSrc, url) {

		const loop     = this.el.dataset.lottieLoop     !== "false";
		const autoplay = this.el.dataset.lottieAutoplay !== "false";
		const speed    = parseFloat(this.el.dataset.lottieSpeed || "1");

		const doRender = () => {
			const player = document.createElement("lottie-player");
			player.setAttribute("src", url);
			player.setAttribute("renderer", this.el.dataset.lottieRenderer || "svg");
			if (loop)     player.setAttribute("loop", "");
			if (autoplay) player.setAttribute("autoplay", "");
			player.setAttribute("speed", String(speed));
			player.style.width  = "100%";
			player.style.height = "100%";

			this.el.innerHTML = "";
			this.el.appendChild(player);
			this._player = player;
		};

		if (customElements.get("lottie-player")) {
			doRender();
			return;
		}

		const existing = Array.from(document.querySelectorAll("script[src]"))
			.find(s => s.getAttribute("src") === scriptSrc);

		if (!existing) {
			const s   = document.createElement("script");
			s.src     = scriptSrc;
			s.type    = "module";
			s.onload  = () => {
				if (customElements.whenDefined) {
					customElements.whenDefined("lottie-player").then(doRender);
				} else {
					setTimeout(doRender, 300);
				}
			};
			s.onerror = () => console.warn("BR Hero: cannot load lottie-player script:", scriptSrc);
			document.head.appendChild(s);
		} else {
			if (customElements.whenDefined) {
				customElements.whenDefined("lottie-player").then(doRender);
			} else {
				setTimeout(doRender, 500);
			}
		}
	}

	_initLottieWeb(url) {

		const lottie = window.lottie;
		if (!lottie || !this.el) return;

		const loop     = this.el.dataset.lottieLoop     !== "false";
		const autoplay = this.el.dataset.lottieAutoplay !== "false";
		const renderer = this.el.dataset.lottieRenderer || "svg";
		const speed    = parseFloat(this.el.dataset.lottieSpeed || "1");

		try {
			this.anim = lottie.loadAnimation({
				container : this.el,
				renderer,
				loop,
				autoplay,
				path      : url,
			});

			if (speed && speed !== 1) this.anim.setSpeed(speed);

			this.anim.addEventListener("data_failed", () => {
				console.warn("BR Hero Lottie: failed to load JSON from:", url);
			});

		} catch (err) {
			console.warn("BR Hero Lottie: loadAnimation threw:", err);
		}
	}

	destroy() {
		if (this.anim)      { this.anim.destroy(); this.anim = null; }
		if (this._observer) { this._observer.disconnect(); this._observer = null; }
	}
}


function initBRHeroLotties(root = document) {
	root.querySelectorAll(".br-hero__lottie:not([data-lottie-loaded])").forEach(el => {
		new BRHeroLottie(el);
	});
}


/* ═══════════════════════════════════════════════════════════
   BACKGROUND SLIDER
═══════════════════════════════════════════════════════════ */

class BRHeroSlider {

	constructor(el) {

		this.el = el;

		if (this.el.dataset.sliderLoaded === "1") return;
		this.el.dataset.sliderLoaded = "1";

		this.slides  = Array.from(el.querySelectorAll(".br-hero__slide"));
		this.current = 0;
		this.speed   = parseInt(el.dataset.sliderSpeed, 10) || 5000;

		if (this.slides.length < 2) return;

		this._interval = window.setInterval(() => this._next(), this.speed);

		this._observer = new MutationObserver(() => {
			if (!document.body.contains(this.el)) this.destroy();
		});
		this._observer.observe(document.body, { childList: true, subtree: true });
	}

	_next() {
		const prev = this.current;
		this.current = (this.current + 1) % this.slides.length;

		this.slides[prev].classList.remove("br-hero__slide--active");
		this.slides[this.current].classList.add("br-hero__slide--active");
	}

	destroy() {
		if (this._interval) { window.clearInterval(this._interval); this._interval = null; }
		if (this._observer) { this._observer.disconnect(); this._observer = null; }
	}
}

function initBRHeroSliders(root = document) {
	root.querySelectorAll(".br-hero__slides:not([data-slider-loaded])").forEach(el => {
		new BRHeroSlider(el);
	});
}


/* ═══════════════════════════════════════════════════════════
   VIDEO — re-trigger autoplay after Ajax / FSE injection
═══════════════════════════════════════════════════════════ */

function initBRHeroVideos(root = document) {
	root.querySelectorAll(".br-hero__video").forEach(video => {
		if (video.dataset.videoLoaded === "1") return;
		video.dataset.videoLoaded = "1";

		if (video.hasAttribute("autoplay")) {
			video.play().catch(() => {
				// Autoplay blocked (unmuted) — silently ignore
			});
		}
	});
}


/* ═══════════════════════════════════════════════════════════
   CORNER LOGOS — stack en colonne seulement si collision
   RÉELLE en 2D (horizontale ET verticale).

   Pourquoi 2D ?
   Les logos (bottom:16px) et le countdown (bottom:-80px)
   se chevauchent toujours verticalement par construction.
   À large écran ils sont séparés horizontalement → pas de
   vraie collision visuelle. On vérifie donc les deux axes.

   Anti-flash :
   Le remove/add de --stacked se fait en synchrone dans le
   même micro-task (avant tout repaint) → zéro flash visible.
═══════════════════════════════════════════════════════════ */

function rectsCollide(a, b, tolerance = 8) {
	const verticalOverlap   = a.bottom > b.top + tolerance;
	const horizontalOverlap = a.right  > b.left && a.left < b.right;
	return verticalOverlap && horizontalOverlap;
}

function adjustCornerLogos(root = document) {
	root.querySelectorAll(".br-hero").forEach(hero => {
		const countdown = hero.querySelector(".br-hero__countdown");

		["bottom-right", "bottom-left"].forEach(pos => {
			const group = hero.querySelector(`.br-hero__corner-group--${pos}`);
			if (!group) return;

			// Pas de countdown visible → retire le stacking si présent
			if (!countdown || countdown.style.display === "none") {
				group.classList.remove("br-hero__corner-group--stacked");
				return;
			}

			const isStacked = group.classList.contains("br-hero__corner-group--stacked");

			if (isStacked) {
				// Teste si on peut revenir en row — retire la classe et mesure.
				// getBoundingClientRect() force un reflow synchrone avant repaint → pas de flash.
				group.classList.remove("br-hero__corner-group--stacked");
				const gRect = group.getBoundingClientRect();
				const cRect = countdown.getBoundingClientRect();
				if (rectsCollide(gRect, cRect)) {
					// Collision toujours là → remet dans le même micro-task
					group.classList.add("br-hero__corner-group--stacked");
				}
			} else {
				// Pas encore stacké → vérifie collision 2D directement
				const gRect = group.getBoundingClientRect();
				const cRect = countdown.getBoundingClientRect();
				if (rectsCollide(gRect, cRect)) {
					group.classList.add("br-hero__corner-group--stacked");
				}
			}
		});
	});
}


/* ═══════════════════════════════════════════════════════════
   BUTTONS

   Desktop (> 768px) :
     - nowrap si tous les boutons tiennent sur une ligne
     - wrap sinon (boutons côte à côte sur 2 lignes)

   Mobile (≤ 768px) :
     - Le CSS gère tout (flex-direction: column, width: 100%)
     - On reset les styles inline pour laisser le CSS prendre
       le contrôle — pas d'interférence du JS
═══════════════════════════════════════════════════════════ */

function adjustHeroButtons(root = document) {
	root.querySelectorAll(".br-hero__buttons").forEach(container => {
		const hero = container.closest(".br-hero");
		if (!hero) return;

		// Mobile — CSS gère tout, on reset les overrides JS
		if (window.innerWidth <= 768) {
			container.style.flexDirection = "";
			container.style.flexWrap      = "";
			return;
		}

		// Desktop — mesure la largeur naturelle des boutons (en nowrap)
		// sans modifier le layout visible
		const heroWidth = hero.getBoundingClientRect().width;
		const gap       = parseFloat(getComputedStyle(container).gap) || 16;
		const buttons   = Array.from(container.querySelectorAll(".ituBtn, a, button"));

		if (!buttons.length) return;

		// Force nowrap + reflow synchrone pour mesurer la largeur réelle
		container.style.flexDirection = "row";
		container.style.flexWrap      = "nowrap";

		const totalWidth = buttons.reduce((acc, btn) => {
			return acc + btn.getBoundingClientRect().width;
		}, 0) + (buttons.length - 1) * gap;

		// 24px de marge de sécurité par rapport au bord du hero
		if (totalWidth > heroWidth - 24) {
			container.style.flexWrap = "wrap";
		}
		// Sinon on garde nowrap — les boutons tiennent sur une ligne
	});
}


/* ═══════════════════════════════════════════════════════════
   CALENDAR BUTTON — génère et télécharge un fichier .ics
═══════════════════════════════════════════════════════════ */

function escapeICS(str) {
	return (str || '')
		.replace(/\\/g, '\\\\')
		.replace(/;/g, '\\;')
		.replace(/,/g, '\\,')
		.replace(/\r?\n/g, '\\n');   // RFC 5545: literal \n represents a newline in property values
}

// RFC 5545 §3.1: fold lines longer than 75 octets (CRLF + SPACE continuation)
function foldICSLine(line) {
	if (line.length <= 75) return line;
	const parts = [line.slice(0, 75)];
	let pos = 75;
	while (pos < line.length) {
		parts.push(line.slice(pos, pos + 74));
		pos += 74;
	}
	return parts.join('\r\n ');
}

function buildICSSummary(eyebrow, title, description) {
	// Compose SUMMARY from eyebrow + title + description (newlines → spaces)
	const descPlain = (description || '').replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
	return [eyebrow, title, descPlain].filter(Boolean).join(' - ');
}

function generateICS({ eyebrow, title, description, dtstart, dtend, location, url }) {
	const summary = buildICSSummary(eyebrow, title, description);
	// Append URL in parentheses to Location so it appears in all calendar clients
	const locationWithUrl = location && url ? `${location} (${url})`
	                      : url             ? url
	                      : location        ? location
	                      :                   null;
	const uid   = `brhero-${dtstart}-${Math.random().toString(36).slice(2)}@br-blocks`;
	const stamp = new Date().toISOString().replace(/[-:.]/g, '').slice(0, 15) + 'Z';
	const lines = [
		'BEGIN:VCALENDAR',
		'VERSION:2.0',
		'PRODID:-//ITU br-blocks//Calendar//EN',
		'CALSCALE:GREGORIAN',
		'METHOD:PUBLISH',
		'BEGIN:VEVENT',
		`DTSTART:${dtstart}`,
		`DTEND:${dtend}`,
		`DTSTAMP:${stamp}`,
		`UID:${uid}`,
		`SUMMARY:${escapeICS(summary)}`,
		description     ? `DESCRIPTION:${escapeICS(description)}` : null,
		locationWithUrl ? `LOCATION:${escapeICS(locationWithUrl)}` : null,
		url             ? `URL:${url}`                             : null,
		'END:VEVENT',
		'END:VCALENDAR',
	];
	return lines.filter(Boolean).map(foldICSLine).join('\r\n');
}

function initCalendarButtons(root = document) {
	root.querySelectorAll('.br-hero__cal-btn').forEach(btn => {
		if (btn.dataset.calLoaded) return;
		btn.dataset.calLoaded = '1';

		btn.addEventListener('click', () => {
			const ics  = generateICS({
				eyebrow:     btn.dataset.eyebrow     || '',
				title:       btn.dataset.title       || '',
				description: btn.dataset.description || '',
				dtstart:     btn.dataset.dtstart,
				dtend:       btn.dataset.dtend,
				location:    btn.dataset.location    || '',
				url:         btn.dataset.url         || '',
			});
			const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
			const href = URL.createObjectURL(blob);
			const a    = Object.assign(document.createElement('a'), { href, download: 'event.ics' });
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			setTimeout(() => URL.revokeObjectURL(href), 1000);
		});
	});
}


/* ═══════════════════════════════════════════════════════════
   INIT – DOM ready
═══════════════════════════════════════════════════════════ */

function initAll(root = document) {
	initBRHeroCountdowns(root);
	initBRHeroLotties(root);
	initBRHeroVideos(root);
	initBRHeroSliders(root);
	adjustCornerLogos(root);
	adjustHeroButtons(root);
	initCalendarButtons(root);
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", () => initAll());
} else {
	initAll();
}

// Recalcule à chaque resize — throttlé via rAF (1 calcul par frame max)
let _resizeRaf = null;
window.addEventListener("resize", () => {
	if (_resizeRaf) return;
	_resizeRaf = requestAnimationFrame(() => {
		adjustCornerLogos();
		adjustHeroButtons();
		_resizeRaf = null;
	});
}, { passive: true });


/* ═══════════════════════════════════════════════════════════
   GLOBAL OBSERVER (WP 6+, patterns, ajax, FSE)

   Filtre les mutations : ignore les changements d'innerHTML
   du countdown (tick chaque seconde) pour éviter de
   déclencher inutilement adjustCornerLogos.
   Réagit uniquement si un vrai nœud .br-hero est ajouté.
═══════════════════════════════════════════════════════════ */

const globalObserver = new MutationObserver((mutations) => {

	let newHeroAdded = false;

	for (const mutation of mutations) {
		for (const node of mutation.addedNodes) {
			if (node.nodeType !== 1) continue;
			if (
				node.classList?.contains("br-hero") ||
				node.querySelector?.(".br-hero")
			) {
				newHeroAdded = true;
				break;
			}
		}
		if (newHeroAdded) break;
	}

	if (!newHeroAdded) return;

	requestAnimationFrame(() => initAll());
});

globalObserver.observe(document.body, { childList: true, subtree: true });